import React, { MouseEvent, ChangeEvent, FormEvent, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { Button, Checkbox, Divider, FormControlLabel, MenuItem, TextField, Typography } from '@material-ui/core';
import { CircleCheckIcon, CircleTimes, PlusIcon, SyncIcon } from 'components/Icons';

import {
  assembleiaAssessorActionsUploadDocument,
  assembleiasAssessorLoadEvento,
} from 'store/modules/AssembleiasAssessorActions';

import ErrorHandler from 'utils/ErrorHandler';
import TextRange from 'utils/TextRange';
import { isValidPertencerFile, isValidFileSize, isValidFileName } from 'utils/File';

import useStyle from './style';
import TipoDocumento from 'models/TipoDocumento';

interface Props {
  idEvento: number;
  categorias?: TipoDocumento[];
  atualizarDocumentos: Function;
}

const MAXLENGHT = 250;

export default function DocumentosModalForm({ categorias, atualizarDocumentos, idEvento }: Props) {
  const { t } = useTranslation();
  const classes = useStyle();
  const dispatch = useDispatch();
  const [fileList, setFileList] = useState<FileList | null>(null);
  const [descricao, setDescricao] = useState<string>('');
  const [ idCategoria, setIdCategoria ] = useState<number>(0);
  const [flgVisivelAssociado, setFlgVisivelAssociado] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const formId = 'input-docs-assessor';
  
  const resetForm = useCallback(() => {
    const form = document.getElementById(formId) as HTMLFormElement;
    if (form) {
      form.reset();
    }
  }, [formId])

  const file = useMemo(() => {
    if (!fileList?.length) return null;
    return fileList[0];
  }, [fileList]);

  const hasFile = useMemo(() => {
    return file !== null;
  }, [file]);

  const fileIsInvalid = useMemo(() => {
    if (!file) return '';
    if (!isValidPertencerFile(file.name)) return 'EditarDocumentosModal.fileError.extension';
    if (!isValidFileSize(file.size, 10)) return 'EditarDocumentosModal.fileError.size';
    if (!isValidFileName(file.name, 200)) return 'EditarDocumentosModal.fileError.name';
    return '';
  }, [file]);

  const handleAddButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    const target = event.currentTarget.nextElementSibling as HTMLInputElement;
    target.click();
  };

  const handleInputFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.currentTarget.files;
    setFileList(files);
  };

  const handleDescricaoChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    event.currentTarget.value = TextRange(event.currentTarget.value.replace(/(\r?\n|\r){2}/g, '\n'), MAXLENGHT);
    setDescricao(event.currentTarget.value);
  };

  const handleFlagChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFlgVisivelAssociado(event.currentTarget.checked);
  };

  const clearForm = useCallback(() => {
    setFileList(null);
    setDescricao('');
    setFlgVisivelAssociado(false);
    setIdCategoria(0);
    resetForm();
  }, [setFileList, setDescricao, setFlgVisivelAssociado, setIdCategoria, resetForm]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!file) return;
      setUploading(true);
      dispatch(
        assembleiaAssessorActionsUploadDocument(
          {
            arquivo: file,
            nome: file.name,
            tamanho: file.size,
            idAssembleia: idEvento,
            flgVisivelAssociado,
            idTipoDocumento: idCategoria!,
            descricao: descricao
          },
          () =>
            dispatch(
              assembleiasAssessorLoadEvento(idEvento, () => {
                clearForm();
                setUploading(false);
                atualizarDocumentos();
              })
            ),
          error => {
            ErrorHandler(error);
            clearForm();
            setUploading(false);
          }
        )
      );
    },
    [dispatch, file, descricao, flgVisivelAssociado, idEvento, idCategoria, atualizarDocumentos, clearForm]
  );

  const handleCurrentTipoDocumentoChange = (tipo: number) => {
    setIdCategoria(tipo);
  };

  return (
    <form id={formId} className={classes.form} onSubmit={handleSubmit}>
      <div className={classes.formFile}>
        <Button
          type="button"
          variant="contained"
          color="primary"
          onClick={handleAddButtonClick}
          startIcon={<PlusIcon />}
          disabled={hasFile && !fileIsInvalid}
        >
          {t('EditarDocumentosModal.buttonAdd')}
        </Button>
        <input type="file" onChange={handleInputFileChange} />
        {hasFile && !fileIsInvalid && (
          <Typography>
            <strong>{file!.name}</strong>
          </Typography>
        )}
        {hasFile && fileIsInvalid && (
          <Typography color="error">
            <strong>{t(fileIsInvalid)}</strong>
          </Typography>
        )}
      </div>
      {hasFile && !fileIsInvalid && (
        <div className={classes.formEditData}>

          <TextField
            label={t('EditarDocumentosModal.labelInputCategoria')}
            SelectProps={{
              value:idCategoria || '',
              onChange: evt => handleCurrentTipoDocumentoChange(evt.target.value as number),
            }}
            disabled={!categorias?.length}
            select
            fullWidth
          >
            {
              categorias!.map((categoria: TipoDocumento) => {
                if(!categoria.flgDeleted){
                  return (
                    <MenuItem key={categoria.id} value={categoria.id} selected={idCategoria === categoria.id} >
                      {categoria.descricao}
                    </MenuItem>
                  )
                } return undefined;
              })
            }
          </TextField>

          <TextField
            label={t('EditarDocumentosModal.labelInputDescricao')}
            value={descricao}
            onChange={handleDescricaoChange}
            rowsMax={3}
            disabled={uploading}
            multiline
            fullWidth
          />
          <FormControlLabel
            label={t('EditarDocumentosModal.labelCheckboxAvailable')}
            control={<Checkbox color="primary" onChange={handleFlagChange} />}
            labelPlacement="start"
            checked={flgVisivelAssociado}
            className={classes.formFlagCheckbox}
            disabled={uploading}
          />
          <div className={classes.formActions}>
            <Button
              type="reset"
              variant="outlined"
              color="primary"
              startIcon={<CircleTimes />}
              onClick={clearForm}
              disabled={uploading}
            >
              {t('EditarDocumentosModal.buttonCancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              startIcon={uploading ? <SyncIcon animated /> : <CircleCheckIcon />}
              disabled={uploading || idCategoria === 0 }
            >
              {t('EditarDocumentosModal.buttonSave')}
            </Button>
          </div>
        </div>
      )}
      <Divider className={classes.formDivider} />
    </form>
  );
}
